import type { TenantContext } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { useTenantContextHook } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useInsightGlobalConfig } from '@atlassian/jira-servicedesk-insight-global-configuration-store/src/services/index.tsx';

/**
 * Hook that provides Assets-specific tenant context with role-based permissions.
 * When the feature gate is enabled, overrides admin permissions with Assets-specific roles.
 */
export const useAssetsTenantContext = (): TenantContext => {
	const [{ tenantContext }] = useTenantContextHook();
	const [{ config: globalConfigData }] = useInsightGlobalConfig();
	const { insightAdministrator: isAssetsAdmin } = globalConfigData ?? {};

	// Override admin permissions with Assets-specific role when feature gate is enabled
	if (fg('assets-new-role-migrations')) {
		return {
			...tenantContext,
			isAdmin: !!isAssetsAdmin,
			isSiteAdmin: !!isAssetsAdmin,
		};
	}
	return tenantContext;
};

/**
 * Hook that returns whether the current user is an Assets administrator.
 */
export const useAssetsIsAdmin = (): boolean => {
	const tenantContext = useAssetsTenantContext();
	return tenantContext?.isAdmin ?? false;
};

/**
 * Hook that returns whether the current user is an Assets site administrator.
 */
export const useAssetsIsSiteAdmin = (): boolean => {
	const tenantContext = useAssetsTenantContext();
	return tenantContext?.isSiteAdmin ?? false;
};
